/**
 * Rhythmz — AIConsole
 * ───────────────────
 * Full-screen AI interaction overlay. Opens via the ⚡ button in the top bar.
 *
 * Contains:
 * 1. Chat pane — type messages, get streaming AI responses
 * 2. Thinking pane — the AI's raw thought process, token by token
 * 3. Action Registry — every action with verification status
 * 4. Progress Tracker — percentage bar for multi-step operations
 * 5. Focus Mode — dedicate all AI resources to a single task
 *
 * Works independently of WebLLM. Uses the edge streaming API directly.
 * No callbacks, no missing dependencies. Just works.
 */

export interface ActionItem {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  detail: string;
  startedAt: number;
  completedAt: number | null;
  result: string | null;
}

export class AIConsole {
  private container: HTMLElement;
  private visible = false;

  // ── Chat ──
  private chatInput!: HTMLInputElement;
  private chatSend!: HTMLButtonElement;
  private chatHistory!: HTMLDivElement;

  // ── Thinking ──
  private thinkingPane!: HTMLElement;
  private thinkingText!: HTMLElement;

  // ── Actions ──
  private actionList!: HTMLElement;
  private actions: ActionItem[] = [];
  private actionIdCounter = 0;

  // ── Progress ──
  private progressFill!: HTMLElement;
  private progressLabel!: HTMLElement;

  // ── Focus ──
  private focusActive = false;
  private focusTask = '';

  /** Callbacks for custom action execution. */
  onExecuteAction: ((label: string, id: string) => Promise<{ success: boolean; detail: string }>) | null = null;

  constructor() {
    this.container = this.build();
    document.body.appendChild(this.container);
    this.bindEvents();
  }

  /* ─── DOM Building ─── */

  private build(): HTMLElement {
    const el = document.createElement('div');
    el.id = 'ai-console';
    el.className = 'ai-console';
    el.style.display = 'none';
    el.innerHTML = `
      <div class="ai-console-header">
        <span class="ai-console-title">🧠 AI Console</span>
        <div class="ai-console-tabs">
          <button class="ac-tab active" data-pane="chat">💬 Chat</button>
          <button class="ac-tab" data-pane="thinking">🧵 Thinking</button>
          <button class="ac-tab" data-pane="actions">📋 Actions</button>
        </div>
        <div class="ai-console-status">
          <span class="ac-model-badge" id="ac-model-badge">Edge AI</span>
          <button class="ac-focus-btn" id="ac-focus-btn" title="Focus Mode: dedicate all resources to current task">🎯 Focus</button>
          <button class="ac-close-btn" id="ac-close">✕</button>
        </div>
      </div>

      <!-- Chat Pane -->
      <div class="ac-pane active" id="ac-pane-chat">
        <div class="ac-chat-history" id="ac-chat-history">
          <div class="ac-chat-empty">
            <div class="ac-chat-empty-icon">🧠</div>
            <span>AI Console ready. Type a message or click a quick action below.</span>
          </div>
        </div>
        <div class="ac-chat-input-row">
          <input type="text" id="ac-chat-input" placeholder="Ask me anything — compose music, build 3D scenes, research, analyze…" />
          <button id="ac-chat-send">▶</button>
        </div>
        <div class="ac-chat-quick">
          <button data-quick="compose a dark techno track at 140bpm">🎼 Compose techno</button>
          <button data-quick="build a neon-lit galaxy scene with pulsing stars">✨ Build galaxy</button>
          <button data-quick="analyze the current audio and recommend visual changes">📊 Analyze audio</button>
          <button data-quick="research the latest WebGPU rendering techniques">🔬 Research</button>
        </div>
      </div>

      <!-- Thinking Pane -->
      <div class="ac-pane" id="ac-pane-thinking">
        <div class="ac-thinking-stream" id="ac-thinking-stream">
          <span class="ac-thinking-empty">AI output will appear here as it generates token by token…</span>
        </div>
      </div>

      <!-- Actions Pane -->
      <div class="ac-pane" id="ac-pane-actions">
        <div class="ac-actions-header">
          <span>Action Registry</span>
          <span class="ac-actions-summary" id="ac-actions-summary">0 actions</span>
        </div>
        <div class="ac-actions-list" id="ac-actions-list"></div>
        <div class="ac-actions-progress">
          <div class="ac-progress-label" id="ac-progress-label">Ready</div>
          <div class="ac-progress-bar"><div class="ac-progress-fill" id="ac-progress-fill"></div></div>
          <span class="ac-progress-pct" id="ac-progress-pct">0%</span>
        </div>
      </div>
    `;

    // Capture references
    this.chatInput = el.querySelector('#ac-chat-input')! as HTMLInputElement;
    this.chatSend = el.querySelector('#ac-chat-send')! as HTMLButtonElement;
    this.chatHistory = el.querySelector('#ac-chat-history')! as HTMLDivElement;
    this.thinkingPane = el.querySelector('#ac-pane-thinking')!;
    this.thinkingText = el.querySelector('#ac-thinking-stream')!;
    this.actionList = el.querySelector('#ac-actions-list')!;
    this.progressFill = el.querySelector('#ac-progress-fill')!;
    this.progressLabel = el.querySelector('#ac-progress-label')!;

    return el;
  }

  /* ─── Events ─── */

  private bindEvents(): void {
    // Close
    this.container.querySelector('#ac-close')?.addEventListener('click', () => this.hide());

    // Tab switching
    this.container.querySelectorAll('.ac-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        const pane = (tab as HTMLElement).dataset.pane;
        this.container.querySelectorAll('.ac-tab').forEach(t => t.classList.remove('active'));
        this.container.querySelectorAll('.ac-pane').forEach(p => p.classList.remove('active'));
        tab.classList.add('active');
        const paneEl = this.container.querySelector(`#ac-pane-${pane}`);
        if (paneEl) paneEl.classList.add('active');
      });
    });

    // Chat send
    this.chatSend.addEventListener('click', () => this.sendChat());
    this.chatInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.sendChat();
    });

    // Quick actions
    this.container.querySelectorAll('[data-quick]').forEach(btn => {
      btn.addEventListener('click', () => {
        const msg = (btn as HTMLElement).dataset.quick;
        if (msg) {
          this.chatInput.value = msg;
          this.sendChat();
        }
      });
    });

    // Focus button
    this.container.querySelector('#ac-focus-btn')?.addEventListener('click', () => this.toggleFocus());
  }

  /* ─── Visibility ─── */

  show(): void {
    this.visible = true;
    this.container.style.display = 'flex';
    setTimeout(() => this.chatInput?.focus(), 100);
  }

  hide(): void {
    this.visible = false;
    this.container.style.display = 'none';
  }

  toggle(): void {
    this.visible ? this.hide() : this.show();
  }

  /* ─── Chat ─── */

  private async sendChat(): Promise<void> {
    const msg = this.chatInput.value.trim();
    if (!msg || this.chatSend.disabled) return;

    this.chatInput.value = '';
    this.chatSend.disabled = true;

    // Remove empty state
    const empty = this.chatHistory.querySelector('.ac-chat-empty');
    if (empty) empty.remove();

    // User message
    this.addChatBubble('user', msg);
    this.addAction('💬 Chat request', msg, 'pending');

    // AI thinking placeholder
    const thinkingId = this.addAction('🧠 AI thinking', 'Generating response...', 'running');

    try {
      const response = await this.streamChat(msg);
      this.addChatBubble('ai', response);
      this.updateAction(thinkingId, '🧠 AI response', 'Response generated', 'completed', response.slice(0, 200));
      this.addAction('✅ Response delivered', `${response.length} characters`, 'completed');
    } catch (e) {
      const err = e instanceof Error ? e.message : String(e);
      this.addChatBubble('ai', `❌ Error: ${err}`);
      this.updateAction(thinkingId, '🧠 AI response', `Error: ${err}`, 'failed', null);
    } finally {
      this.chatSend.disabled = false;
      this.chatInput.focus();
    }
  }

  /** Stream chat response from the edge gateway. */
  private async streamChat(prompt: string): Promise<string> {
    const resp = await fetch('/ai/stream', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        prompt,
        system: 'You are the AI director for Rhythmz, a premium audio visualizer. You compose music, build 3D scenes, analyze audio, and research. Respond helpfully and creatively. Be specific — mention BPM, colors, objects, or techniques when relevant.',
        max_tokens: 800,
        temperature: 0.75,
      }),
    });

    if (!resp.ok) throw new Error(`API error: ${resp.status}`);

    const reader = resp.body?.getReader();
    if (!reader) throw new Error('No response stream');

    let fullText = '';
    const bubble = this.addChatBubble('ai', '');
    const textEl = bubble.querySelector('.ac-bubble-text')!;

    // Switch to thinking pane to show raw tokens
    this.clearThinking();

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const data = JSON.parse(line.slice(6));
          if (data.error) throw new Error(data.error);
          if (data.done) break;
          if (data.token) {
            fullText += data.token;
            textEl.textContent = fullText;

            // Feed to thinking pane
            this.addThinking(data.token);

            this.chatHistory.scrollTop = this.chatHistory.scrollHeight;
          }
        } catch { /* skip */ }
      }
    }

    return fullText;
  }

  private addChatBubble(role: 'user' | 'ai', text: string): HTMLElement {
    const bubble = document.createElement('div');
    bubble.className = `ac-bubble ac-bubble-${role}`;
    bubble.innerHTML = `<div class="ac-bubble-role">${role === 'user' ? 'YOU' : 'AI'}</div><div class="ac-bubble-text">${text}</div>`;
    this.chatHistory.appendChild(bubble);
    this.chatHistory.scrollTop = this.chatHistory.scrollHeight;
    return bubble;
  }

  /* ─── Thinking ─── */

  clearThinking(): void {
    this.thinkingText.innerHTML = '';
  }

  addThinking(token: string): void {
    // Append token to thinking stream
    const span = document.createElement('span');
    span.textContent = token;
    this.thinkingText.appendChild(span);
    this.thinkingPane.scrollTop = this.thinkingPane.scrollHeight;
  }

  /* ─── Actions ─── */

  addAction(label: string, detail: string, status: 'pending' | 'running' | 'completed' | 'failed'): string {
    const id = `action-${++this.actionIdCounter}`;
    const now = Date.now();

    const action: ActionItem = {
      id, label, status, detail,
      startedAt: now,
      completedAt: status === 'completed' || status === 'failed' ? now : null,
      result: status === 'completed' ? detail : null,
    };

    this.actions.push(action);

    // Update the list UI
    const entry = document.createElement('div');
    entry.className = `ac-action ac-action-${status}`;
    entry.id = id;
    entry.innerHTML = `
      <span class="ac-action-status">${status === 'pending' ? '○' : status === 'running' ? '◉' : status === 'completed' ? '●' : '✕'}</span>
      <span class="ac-action-label">${label}</span>
      <span class="ac-action-detail">${detail}</span>
    `;
    this.actionList.appendChild(entry);
    this.actionList.scrollTop = this.actionList.scrollHeight;

    // Update summary count
    const summary = this.container.querySelector('#ac-actions-summary');
    if (summary) summary.textContent = `${this.actions.length} actions`;

    // Update progress
    this.recalculateProgress();

    return id;
  }

  updateAction(id: string, label: string, detail: string, status: 'completed' | 'failed' | 'running', result: string | null): void {
    const el = this.container.querySelector(`#${id}`);
    if (el) {
      el.className = `ac-action ac-action-${status}`;
      const statusEl = el.querySelector('.ac-action-status');
      if (statusEl) statusEl.textContent = status === 'completed' ? '●' : status === 'failed' ? '✕' : '◉';
      const labelEl = el.querySelector('.ac-action-label');
      if (labelEl) labelEl.textContent = label;
      const detailEl = el.querySelector('.ac-action-detail');
      if (detailEl) detailEl.textContent = detail;
    }

    const action = this.actions.find(a => a.id === id);
    if (action) {
      action.status = status;
      action.completedAt = status === 'completed' || status === 'failed' ? Date.now() : null;
      action.result = result;
    }

    this.recalculateProgress();
  }

  recalculateProgress(): void {
    const total = this.actions.length;
    if (total === 0) {
      this.setProgress(0, 'Ready');
      return;
    }

    const completed = this.actions.filter(a => a.status === 'completed').length;
    const failed = this.actions.filter(a => a.status === 'failed').length;
    const running = this.actions.filter(a => a.status === 'running').length;
    const pct = Math.round(((completed + failed) / total) * 100);

    let label = '';
    if (running > 0) label = `Running: ${this.actions.find(a => a.status === 'running')?.label ?? '…'} (${pct}%)`;
    else label = `${completed} completed${failed > 0 ? `, ${failed} failed` : ''}`;

    this.setProgress(pct, label);
  }

  setProgress(pct: number, label: string): void {
    this.progressFill.style.width = `${pct}%`;
    this.progressLabel.textContent = label;
    const pctEl = this.container.querySelector('#ac-progress-pct');
    if (pctEl) pctEl.textContent = `${pct}%`;
  }

  /* ─── Focus Mode ─── */

  toggleFocus(): void {
    this.focusActive = !this.focusActive;
    const btn = this.container.querySelector('#ac-focus-btn');
    const badge = this.container.querySelector('#ac-model-badge');

    if (this.focusActive) {
      this.focusTask = this.chatInput.value || 'Current task';
      btn!.textContent = '🔴 Focus ON';
      btn!.classList.add('ac-focus-active');
      badge!.textContent = 'FOCUS MODE';
      badge!.classList.add('ac-model-badge-focus');
      this.addAction('🎯 Focus enabled', `Dedicating all resources to: "${this.focusTask}"`, 'running');
    } else {
      btn!.textContent = '🎯 Focus';
      btn!.classList.remove('ac-focus-active');
      badge!.textContent = 'Edge AI';
      badge!.classList.remove('ac-model-badge-focus');
      this.addAction('🎯 Focus disabled', 'Resuming normal operation', 'completed');
    }
  }

  /** Get the AI console HTML element for potential external interaction. */
  get element(): HTMLElement { return this.container; }

  /** Whether the console is visible. */
  get isVisible(): boolean { return this.visible; }
}